// [PURPOSE]: Off-list scroll casting detection helpers (Sub-plan 4M — 2026-05-28)
// [WHY]: DMG p.139 — RAW a spell scroll is unintelligible if the spell is not on
//   the reader's class spell list. The house-rule opt-in (enableOffListCasting)
//   gates an Arcana check (DC 10 + spell level) to allow off-list attempts.
// [INTEGRATION]: Consumed by App.jsx readScrollSpell (cast-flow gate) and the
//   OffListScrollCastModal component. Also re-usable for scroll picker hints
//   or spellbook-browser filtering.

import { SRD_SPELLS } from '../data/srdSpells';

/**
 * Returns true if ANY of the character's classes include this spell on their list.
 *
 * Detection strategy (in order):
 *   1. SRD_SPELLS — check spell.classes[] from the canonical data, matched by
 *      player's class name (case-insensitive).
 *   2. Custom spells — check playerStats.customSpells[].classes[] with the same
 *      logic so Content Workshop spells work transparently (Rule 16 / Rule 22).
 *
 * @param {Object|string} spell  - Spell object (must have .name and optionally .level)
 *                                 OR plain spell name string.
 * @param {Object} playerStats   - Character playerStats with classes[] array.
 * @returns {boolean}
 */
export function isSpellOnClassList(spell, playerStats) {
  // Null-safety per contract test 9
  if (!spell || !playerStats) return false;

  const spellName = typeof spell === 'string' ? spell : spell.name;
  if (!spellName) return false;

  const classes = Array.isArray(playerStats.classes) ? playerStats.classes : [];
  if (classes.length === 0) return false;

  const spellNameLower = spellName.toLowerCase();

  // Collect all class names this character has levels in
  const classNames = classes
    .map(c => (typeof c === 'string' ? c : c?.className) || '')
    .filter(Boolean);

  if (classNames.length === 0) return false;

  // Scan SRD_SPELLS by level bucket (flat lookup by name)
  const allLevels = Object.values(SRD_SPELLS);
  for (const levelArray of allLevels) {
    for (const srdSpell of levelArray) {
      if ((srdSpell.name || '').toLowerCase() !== spellNameLower) continue;
      // Found matching SRD spell — check its class list
      const spellClasses = Array.isArray(srdSpell.classes) ? srdSpell.classes : [];
      const isOnList = classNames.some(cn =>
        spellClasses.some(sc => sc.toLowerCase() === cn.toLowerCase())
      );
      if (isOnList) return true;
    }
  }

  // Also check custom spells (Content Workshop parity — Rule 16)
  const customSpells = Array.isArray(playerStats.customSpells) ? playerStats.customSpells : [];
  for (const customSpell of customSpells) {
    if ((customSpell.name || '').toLowerCase() !== spellNameLower) continue;
    const spellClasses = Array.isArray(customSpell.classes) ? customSpell.classes : [];
    const isOnList = classNames.some(cn =>
      spellClasses.some(sc => sc.toLowerCase() === cn.toLowerCase())
    );
    if (isOnList) return true;
  }

  return false;
}

/**
 * Returns the Arcana DC for an off-list scroll cast attempt.
 *
 * DMG p.139 house rule: DC = 10 + spell level.
 * Cantrips (level 0) → DC 10.
 *
 * @param {Object|number} spell  - Spell object with optional .level, OR plain level number.
 * @returns {number}
 */
export function getOffListArcanaDC(spell) {
  let level = 0;
  if (typeof spell === 'number') {
    level = spell;
  } else if (spell && typeof spell.level === 'number') {
    level = spell.level;
  } else if (spell && typeof spell.level === 'string') {
    level = parseInt(spell.level, 10) || 0;
  }
  return 10 + Math.max(0, level);
}
